// =====================================================
// SavedPostsModal — design identique à profil.tsx
// thumb-up MaterialCommunityIcons + glow violet
// ShareButton + VideoView expo-video + SafeImage
// =====================================================

import React, { useState, useEffect, useRef } from 'react';
import {
  StyleSheet, Text, View, Modal, TouchableOpacity,
  ScrollView, Image, Platform, ActivityIndicator, Dimensions, Animated,
} from 'react-native';
import { Ionicons }               from '@expo/vector-icons';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import * as Haptics               from 'expo-haptics';
import AsyncStorage               from '@react-native-async-storage/async-storage';
import { VideoView, useVideoPlayer } from 'expo-video';
import { useTranslation }          from 'react-i18next';
import ShareButton                from '../components/ShareButton';

const BACKEND_URL = 'https://eueke282zksk1zki18susjdksisk18sj.onrender.com';
const HOME_URL    = `${BACKEND_URL}/home`;
const { width, height } = Dimensions.get('window');
const NATIVE            = Platform.OS !== 'web';
const LIKE_ACTIVE_COLOR   = '#7B1FA2';
const LIKE_INACTIVE_COLOR = '#65676B';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getAccessToken = async (): Promise<string | null> => {
  try {
    const raw = await AsyncStorage.getItem('harmonia_session');
    if (!raw) return null;
    return JSON.parse(raw).access_token ?? null;
  } catch { return null; }
};

const formatTimeAgo = (dateString: string, t: (key: string, opts?: any) => string) => {
  const diff = Math.floor((Date.now() - new Date(dateString).getTime()) / 1000);
  if (diff < 60)    return t('savedPostsModal.time.now');
  if (diff < 3600)  return t('savedPostsModal.time.minutes', { count: Math.floor(diff / 60) });
  if (diff < 86400) return t('savedPostsModal.time.hours', { count: Math.floor(diff / 3600) });
  return t('savedPostsModal.time.days', { count: Math.floor(diff / 86400) });
};

const getInitials = (nom: string, prenom: string) =>
  `${prenom.charAt(0)}${nom.charAt(0)}`.toUpperCase();

// ─── Types ────────────────────────────────────────────────────────────────────
interface Post {
  item_type:   'post';
  id:          string;
  author_id:   string;
  author:      { nom: string; prenom: string; avatar_url: string | null };
  content:     string;
  visibility:  string;
  created_at:  string;
  is_shared?:  boolean;
  imagepots?:  string | null;
  vidposts?:   string | null;
  reactions:   { likes: number; comments: number; shares: number };
  user_liked:  boolean;
  user_shared: boolean;
  user_saved:  boolean;
}

interface SavedPostsModalProps {
  visible:        boolean;
  userId:         string;
  onClose:        () => void;
  onCommentPress: (postId: string) => void;
}

// ─── SafeImage ────────────────────────────────────────────────────────────────
function SafeImage({ uri, style, resizeMode = 'cover', fallback = null }: {
  uri: string | null | undefined;
  style: any;
  resizeMode?: 'cover' | 'contain' | 'stretch' | 'center';
  fallback?: React.ReactNode;
}) {
  const [error, setError] = useState(false);
  useEffect(() => { setError(false); }, [uri]);
  if (!uri || error) return fallback ? <>{fallback}</> : null;
  return (
    <Image
      source={{ uri }}
      style={style}
      resizeMode={resizeMode}
      onError={() => setError(true)}
    />
  );
}

// ─── MobileVideoPlayer ────────────────────────────────────────────────────────
function MobileVideoPlayer({ url }: { url: string }) {
  const player = useVideoPlayer({ uri: url }, p => { p.play(); });
  return (
    <VideoView
      player={player}
      style={mvStyles.nativeVideo}
      contentFit="contain"
      nativeControls
    />
  );
}

// ─── MediaViewer ─────────────────────────────────────────────────────────────
interface MediaViewerProps {
  visible: boolean;
  url: string | null;
  type: 'image' | 'video';
  onClose: () => void;
}

function MediaViewer({ visible, url, type, onClose }: MediaViewerProps) {
  if (!url) return null;
  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose} statusBarTranslucent>
      <View style={mvStyles.overlay}>
        <TouchableOpacity style={mvStyles.closeBtn} onPress={onClose}>
          <Ionicons name="close" size={30} color="#fff" />
        </TouchableOpacity>
        {type === 'image' ? (
          <TouchableOpacity activeOpacity={1} onPress={onClose} style={mvStyles.imageWrapper}>
            <SafeImage uri={url} style={mvStyles.image} resizeMode="contain" />
          </TouchableOpacity>
        ) : Platform.OS === 'web' ? (
          // @ts-ignore
          <video src={url} controls autoPlay style={{ width: '100%', maxHeight: '90%', objectFit: 'contain', backgroundColor: '#000' }} />
        ) : (
          <MobileVideoPlayer url={url} />
        )}
      </View>
    </Modal>
  );
}

const mvStyles = StyleSheet.create({
  overlay:      { flex: 1, backgroundColor: 'rgba(0,0,0,0.97)', justifyContent: 'center', alignItems: 'center' },
  closeBtn:     { position: 'absolute', top: Platform.OS === 'ios' ? 60 : 40, right: 20, zIndex: 10, padding: 8, backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 20 },
  imageWrapper: { width, height: height * 0.85, justifyContent: 'center', alignItems: 'center' },
  image:        { width, height: height * 0.85 },
  nativeVideo:  { width, height: height * 0.75 },
});

// ─── LikeButton — identique à profil.tsx ─────────────────────────────────────
function LikeButton({ liked, isAnim, onPress }: {
  liked: boolean;
  isAnim: boolean;
  onPress: () => void;
}) {
  const scaleAnim = useRef(new Animated.Value(1)).current;
  const glowAnim  = useRef(new Animated.Value(0)).current;

  const handlePress = () => {
    if (isAnim) return;
    Animated.parallel([
      Animated.sequence([
        Animated.timing(scaleAnim, { toValue: 1.3, duration: 120, useNativeDriver: true }),
        Animated.spring(scaleAnim, { toValue: 1, friction: 4, tension: 50, useNativeDriver: true }),
      ]),
      Animated.sequence([
        Animated.timing(glowAnim, { toValue: 1, duration: 120, useNativeDriver: false }),
        Animated.timing(glowAnim, { toValue: 0, duration: 400, useNativeDriver: false }),
      ]),
    ]).start();
    onPress();
  };

  const glowColor = glowAnim.interpolate({
    inputRange:  [0, 1],
    outputRange: ['rgba(123,31,162,0)', 'rgba(123,31,162,0.18)'],
  });

  return (
    <TouchableOpacity style={styles.actionBtnModern} onPress={handlePress} disabled={isAnim} activeOpacity={0.7}>
      <Animated.View style={[
        styles.actionContentModern,
        styles.likeBtnInner,
        liked && styles.likeBtnActive,
        { backgroundColor: glowColor as any },
      ]}>
        <Animated.View style={{ transform: [{ scale: scaleAnim }] }}>
          <MaterialCommunityIcons
            name={liked ? 'thumb-up' : 'thumb-up-outline'}
            size={26}
            color={liked ? LIKE_ACTIVE_COLOR : LIKE_INACTIVE_COLOR}
          />
        </Animated.View>
      </Animated.View>
    </TouchableOpacity>
  );
}

// ─── SavedPostCard ────────────────────────────────────────────────────────────
function SavedPostCard({
  post,
  userId,
  onUnsave,
  onLike,
  onShare,
  onCommentPress,
  t,
}: {
  post:           Post;
  userId:         string;
  onUnsave:       (postId: string) => void;
  onLike:         (postId: string, liked: boolean) => Promise<void>;
  onShare:        (postId: string, shared: boolean) => Promise<void>;
  onCommentPress: (postId: string) => void;
  t:              (key: string, opts?: any) => string;
}) {
  const [liked,       setLiked]       = useState(post.user_liked);
  const [shared,      setShared]      = useState(post.user_shared);
  const [likesCount,  setLikesCount]  = useState(post.reactions.likes);
  const [sharesCount, setSharesCount] = useState(post.reactions.shares);
  const [isAnim,      setIsAnim]      = useState(false);
  const [viewerUrl,   setViewerUrl]   = useState<string | null>(null);
  const [viewerType,  setViewerType]  = useState<'image' | 'video'>('image');

  useEffect(() => {
    setLiked(post.user_liked);
    setShared(post.user_shared);
    setLikesCount(post.reactions.likes);
    setSharesCount(post.reactions.shares);
  }, [post.id, post.user_liked, post.user_shared, post.reactions]);

  const handleLike = async () => {
    if (isAnim) return;
    setIsAnim(true);
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    const next = !liked;
    setLiked(next); setLikesCount(p => next ? p + 1 : p - 1);
    try   { await onLike(post.id, next); }
    catch { setLiked(!next); setLikesCount(p => next ? p - 1 : p + 1); }
    finally { setTimeout(() => setIsAnim(false), 300); }
  };

  const handleShare = async (postId: string, sh: boolean) => {
    const next = sh;
    setShared(next); setSharesCount(p => next ? p + 1 : p - 1);
    try   { await onShare(postId, next); }
    catch { setShared(!next); setSharesCount(p => next ? p - 1 : p + 1); }
  };

  return (
    <>
      <MediaViewer visible={!!viewerUrl} url={viewerUrl} type={viewerType} onClose={() => setViewerUrl(null)} />

      <View style={styles.postCard}>

        {/* HEADER */}
        <View style={styles.postHeader}>
          <View style={styles.postAuthor}>
            <SafeImage
              uri={post.author.avatar_url}
              style={styles.postAvatar}
              resizeMode="cover"
              fallback={
                <View style={styles.postAvatarPlaceholder}>
                  <Text style={styles.postAvatarText}>{getInitials(post.author.nom, post.author.prenom)}</Text>
                </View>
              }
            />
            <View>
              <Text style={styles.postAuthorName}>{post.author.prenom} {post.author.nom}</Text>
              <Text style={styles.postTime}>{formatTimeAgo(post.created_at, t)}</Text>
            </View>
          </View>
          <TouchableOpacity onPress={() => onUnsave(post.id)}>
            <Ionicons name="bookmark" size={22} color="#FFD700" />
          </TouchableOpacity>
        </View>

        {/* CONTENU */}
        <Text style={styles.postContent}>{post.content}</Text>

        {/* IMAGE */}
        {post.imagepots && (
          <TouchableOpacity
            activeOpacity={0.92}
            onPress={() => { setViewerType('image'); setViewerUrl(post.imagepots!); }}
          >
            <SafeImage uri={post.imagepots} style={styles.postImage} resizeMode="cover" />
            <View style={styles.mediaExpandHint}>
              <Ionicons name="expand-outline" size={16} color="rgba(255,255,255,0.85)" />
            </View>
          </TouchableOpacity>
        )}

        {/* VIDÉO */}
        {post.vidposts && (
          <TouchableOpacity
            style={styles.videoThumb}
            activeOpacity={0.92}
            onPress={() => { setViewerType('video'); setViewerUrl(post.vidposts!); }}
          >
            <Ionicons name="play-circle" size={52} color="rgba(255,255,255,0.9)" />
            <Text style={styles.videoThumbLabel}>{t('savedPostsModal.tapToPlay')}</Text>
          </TouchableOpacity>
        )}

        {/* STATS BAR */}
        <View style={styles.statsBar}>
          {likesCount > 0 ? (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
              <View style={styles.statsLikeCircle}>
                <MaterialCommunityIcons name="thumb-up" size={10} color="#FFF" />
              </View>
              <Text style={styles.statsText}>{likesCount}</Text>
            </View>
          ) : <Text style={styles.statsText}></Text>}
          <View style={styles.statsRight}>
            {post.reactions.comments > 0 && (
              <Text style={styles.statsText}>{t('savedPostsModal.commentsCount', { count: post.reactions.comments })}</Text>
            )}
            {post.reactions.comments > 0 && sharesCount > 0 && (
              <Text style={styles.statsSeparator}>•</Text>
            )}
            {sharesCount > 0 && (
              <Text style={styles.statsText}>{t('savedPostsModal.sharesCount', { count: sharesCount })}</Text>
            )}
          </View>
        </View>

        {/* ACTIONS BAR */}
        <View style={styles.actionsBar}>

          {/* LIKE */}
          <LikeButton liked={liked} isAnim={isAnim} onPress={handleLike} />

          {/* COMMENTER */}
          <TouchableOpacity
            style={styles.actionBtnModern}
            onPress={() => {
              if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
              onCommentPress(post.id);
            }}
            activeOpacity={0.6}
          >
            <View style={styles.actionContentModern}>
              <Ionicons name="chatbubble-outline" size={22} color="#65676B" />
            </View>
          </TouchableOpacity>

          {/* PARTAGER */}
          {(post.visibility === 'public' || post.visibility === 'pro') && (
            <ShareButton
              postId={post.id}
              content={post.content}
              isPro={post.visibility === 'pro'}
              shared={shared}
              sharesCount={sharesCount}
              onShareHarmonia={handleShare}
            />
          )}

          {/* SAUVEGARDER — bookmark actif (doré) car déjà sauvegardé */}
          <TouchableOpacity
            style={[styles.actionBtnModern, { flex: 0, paddingHorizontal: 12 }]}
            onPress={() => onUnsave(post.id)}
            activeOpacity={0.6}
          >
            <View style={styles.actionContentModern}>
              <Ionicons name="bookmark" size={22} color="#FFD700" />
            </View>
          </TouchableOpacity>

        </View>
      </View>
    </>
  );
}

// ─── COMPOSANT PRINCIPAL ──────────────────────────────────────────────────────
export default function SavedPostsModal({
  visible, userId, onClose, onCommentPress,
}: SavedPostsModalProps) {
  const { t } = useTranslation();
  const [posts,        setPosts]        = useState<Post[]>([]);
  const [loading,      setLoading]      = useState(false);
  const [initializing, setInitializing] = useState(false);

  useEffect(() => {
    if (visible && userId) {
      setInitializing(true);
      loadSavedPosts().finally(() => setInitializing(false));
    } else {
      setPosts([]); setInitializing(false);
    }
  }, [visible, userId]);

  const loadSavedPosts = async () => {
    setLoading(true);
    try {
      const access_token = await getAccessToken();
      const res  = await fetch(HOME_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'get-saved-posts', user_id: userId, access_token }),
      });
      const data = await res.json();
      if (data.success && data.posts) setPosts(data.posts);
    } catch (err) { console.error('[SavedPostsModal] loadSavedPosts:', err); }
    finally { setLoading(false); }
  };

  const handleUnsave = async (postId: string) => {
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    setPosts(prev => prev.filter(p => p.id !== postId));
    try {
      const access_token = await getAccessToken();
      await fetch(HOME_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'unsave-post', user_id: userId, post_id: postId, access_token }),
      });
    } catch { loadSavedPosts(); }
  };

  const handleLike = async (postId: string, liked: boolean) => {
    try {
      const access_token = await getAccessToken();
      const res  = await fetch(HOME_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: liked ? 'like-post' : 'unlike-post',
          user_id: userId, post_id: postId, access_token,
        }),
      });
      const data = await res.json();
      if (data.success) setPosts(prev => prev.map(p =>
        p.id === postId
          ? { ...p, reactions: { ...p.reactions, likes: data.likes }, user_liked: liked }
          : p
      ));
    } catch (err) { console.error('[SavedPostsModal] handleLike:', err); }
  };

  const handleShare = async (postId: string, shared: boolean) => {
    try {
      const access_token = await getAccessToken();
      const res  = await fetch(HOME_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: shared ? 'share-post' : 'unshare-post',
          user_id: userId, post_id: postId, access_token,
        }),
      });
      const data = await res.json();
      if (data.success) setPosts(prev => prev.map(p =>
        p.id === postId
          ? { ...p, reactions: { ...p.reactions, shares: data.shares }, user_shared: shared }
          : p
      ));
    } catch (err) { console.error('[SavedPostsModal] handleShare:', err); }
  };

  return (
    <Modal visible={visible} animationType="slide" transparent={false} onRequestClose={onClose}>
      <View style={styles.container}>

        {/* HEADER */}
        <View style={styles.header}>
          <TouchableOpacity onPress={onClose} style={styles.backButton}>
            <Ionicons name="chevron-back" size={28} color="#FFF" />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>{t('savedPostsModal.header.title')}</Text>
          <View style={styles.placeholder} />
        </View>

        {initializing ? (
          <View style={styles.initContainer}>
            <ActivityIndicator size="large" color="#7B1FA2" />
            <Text style={styles.initText}>{t('savedPostsModal.syncing')}</Text>
          </View>
        ) : (
          <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
            {loading ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color="#7B1FA2" />
              </View>
            ) : posts.length === 0 ? (
              <View style={styles.emptyContainer}>
                <Ionicons name="bookmark-outline" size={80} color="#CCC" />
                <Text style={styles.emptyText}>{t('savedPostsModal.empty.title')}</Text>
                <Text style={styles.emptySubtext}>{t('savedPostsModal.empty.subtitle')}</Text>
              </View>
            ) : (
              <View style={{ paddingVertical: 8, paddingBottom: 40 }}>
                {posts.map(post => (
                  <SavedPostCard
                    key={post.id}
                    post={post}
                    userId={userId}
                    onUnsave={handleUnsave}
                    onLike={handleLike}
                    onShare={handleShare}
                    onCommentPress={onCommentPress}
                    t={t}
                  />
                ))}
              </View>
            )}
          </ScrollView>
        )}
      </View>
    </Modal>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  container:   { flex: 1, backgroundColor: '#F5F5F5' },
  header:      {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    paddingTop: Platform.OS === 'ios' ? 55 : 20, paddingBottom: 16,
    paddingHorizontal: 16, backgroundColor: '#7B1FA2',
  },
  backButton:  { padding: 4 },
  headerTitle: { fontSize: 18, fontWeight: 'bold', color: '#FFF' },
  placeholder: { width: 36 },
  scrollView:  { flex: 1 },

  loadingContainer: { paddingVertical: 60, alignItems: 'center' },
  emptyContainer:   { alignItems: 'center', paddingVertical: 80, paddingHorizontal: 40 },
  emptyText:        { fontSize: 18, fontWeight: '600', color: '#999', marginTop: 20 },
  emptySubtext:     { fontSize: 14, color: '#CCC', marginTop: 8, textAlign: 'center' },
  initContainer:    { flex: 1, justifyContent: 'center', alignItems: 'center', gap: 16, backgroundColor: '#F5F5F5' },
  initText:         { fontSize: 15, fontWeight: '500', color: '#7B1FA2', letterSpacing: 0.3 },

  // PostCard
  postCard: {
    backgroundColor: '#FFF', marginHorizontal: 12, marginVertical: 6,
    borderRadius: 14, paddingVertical: 14,
    ...Platform.select({
      ios:     { shadowColor: '#7B1FA2', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.08, shadowRadius: 8 },
      android: { elevation: 4 },
    }),
  },
  postHeader:            { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingHorizontal: 14, marginBottom: 10 },
  postAuthor:            { flexDirection: 'row', alignItems: 'center', flex: 1, gap: 10 },
  postAvatar:            { width: 40, height: 40, borderRadius: 20 },
  postAvatarPlaceholder: { width: 40, height: 40, borderRadius: 20, backgroundColor: '#8A2BE2', justifyContent: 'center', alignItems: 'center' },
  postAvatarText:        { color: '#fff', fontSize: 14, fontWeight: 'bold' },
  postAuthorName:        { fontSize: 14, fontWeight: '600', color: '#1A1A1A' },
  postTime:              { fontSize: 12, color: '#999', marginTop: 1 },
  postContent:           { fontSize: 14, color: '#333', lineHeight: 20, paddingHorizontal: 14, marginBottom: 10 },

  postImage:       { width: '100%', height: 240, marginBottom: 10 },
  mediaExpandHint: { position: 'absolute', bottom: 18, right: 10, backgroundColor: 'rgba(0,0,0,0.45)', borderRadius: 12, padding: 4 },
  videoThumb:      { width: '100%', height: 180, backgroundColor: '#1A1A2E', justifyContent: 'center', alignItems: 'center', marginBottom: 10, gap: 8 },
  videoThumbLabel: { color: 'rgba(255,255,255,0.75)', fontSize: 13 },

  // Stats bar
  statsBar:        { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingHorizontal: 14, paddingVertical: 10 },
  statsLikeCircle: { backgroundColor: '#7B1FA2', width: 18, height: 18, borderRadius: 9, justifyContent: 'center', alignItems: 'center' },
  statsText:       { fontSize: 13, color: '#65676B' },
  statsRight:      { flexDirection: 'row', alignItems: 'center', gap: 5 },
  statsSeparator:  { color: '#CCC' },

  // Actions bar — identique à profil.tsx
  actionsBar:          { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-evenly', borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: '#CED0D4', paddingTop: 4, paddingBottom: 4, marginHorizontal: 12, marginTop: 4 },
  actionBtnModern:     { flex: 1, paddingVertical: 8, borderRadius: 8 },
  actionContentModern: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6 },
  likeBtnInner:        { borderRadius: 8, paddingVertical: 4, paddingHorizontal: 8 },
  likeBtnActive:       { shadowColor: '#7B1FA2', shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0.35, shadowRadius: 8, elevation: 4 },
});
